import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// Consulate staff have their own self-contained area. They are sent there from any
// route that does not explicitly list the 'consulate' role, and nobody else may
// enter it. The backend enforces the same boundary; this only keeps the UI honest.
export const CONSULATE_HOME = '/consulate';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const validRoles = requiredRole ? (Array.isArray(requiredRole) ? requiredRole : [requiredRole]) : null;
  const isConsulateUser = user?.role === 'consulate';
  const isConsulateRoute = !!validRoles?.includes('consulate');

  // Where this user should be sent instead, or null if they may see the route.
  let redirectTo = null;
  if (!loading && isAuthenticated) {
    if (isConsulateUser && !isConsulateRoute) redirectTo = CONSULATE_HOME;
    else if (!isConsulateUser && isConsulateRoute) redirectTo = '/dashboard';
    else if (validRoles && !validRoles.includes(user?.role)) redirectTo = isConsulateUser ? CONSULATE_HOME : '/dashboard';
  }

  useEffect(() => {
    // Skip if user data was passed from AuthCallback
    if (location.state?.user) return;

    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
  }, [loading, isAuthenticated, navigate, location]);

  useEffect(() => {
    if (redirectTo && redirectTo !== location.pathname) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ags-bg">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-ags-blue mx-auto mb-4" />
          <p className="text-ags-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Render nothing while redirecting so a disallowed page never flashes on screen.
  if (!isAuthenticated || redirectTo) {
    return null;
  }

  return children;
};

export default ProtectedRoute;
